import { useState } from 'react';
import { User } from '@/types';
import { offerLetterApi } from '@/lib/api';
import { downloadBlob } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { XMarkIcon } from '@heroicons/react/24/outline';

const DEFAULT_EMPLOYMENT_SUMMARY =
  'You were responsible for software development, system maintenance, application enhancements, collaboration with internal teams, and supporting organizational objectives through technical contributions.';

interface RelievingLetterModalProps {
  employee: User;
  onClose: () => void;
}

type SubmitAction = 'download' | 'send';

function todayInputDate(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

async function handleApiError(error: unknown, fallbackMessage: string): Promise<void> {
  console.error(fallbackMessage, error);
  const axiosError = error as {
    response?: { data?: Blob | { error?: string } };
  };
  const data = axiosError.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      alert(parsed.error || fallbackMessage);
      return;
    } catch {
      // fall through
    }
  } else if (data && typeof data === 'object' && 'error' in data) {
    alert(data.error || fallbackMessage);
    return;
  }
  alert(fallbackMessage);
}

export default function RelievingLetterModal({ employee, onClose }: RelievingLetterModalProps) {
  const [lastWorkingDay, setLastWorkingDay] = useState(todayInputDate());
  const [employmentSummary, setEmploymentSummary] = useState(DEFAULT_EMPLOYMENT_SUMMARY);
  const [authorizedSignatory, setAuthorizedSignatory] = useState('Authorized Signatory');
  const [emailTo, setEmailTo] = useState(employee.email || '');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [loading, setLoading] = useState<SubmitAction | null>(null);

  const buildPayload = () => ({
    lastWorkingDay,
    employmentSummary: employmentSummary.trim(),
    ...(authorizedSignatory.trim()
      ? { authorizedSignatory: authorizedSignatory.trim() }
      : {}),
  });

  const validateForm = (): boolean => {
    if (!lastWorkingDay) {
      alert('Last working day is required.');
      return false;
    }

    if (!employmentSummary.trim()) {
      alert('Employment summary is required.');
      return false;
    }

    return true;
  };

  const handleDownload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading('download');
    try {
      const blob = await offerLetterApi.generateRelievingLetter(
        employee._id,
        buildPayload(),
      );

      downloadBlob(
        blob,
        `relieving-letter-${employee.firstName}-${employee.lastName}.pdf`,
      );
      onClose();
    } catch (error: unknown) {
      await handleApiError(error, 'Failed to generate relieving letter. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleSendEmail = async () => {
    if (!validateForm()) return;

    if (!emailTo.trim()) {
      alert('Recipient email is required.');
      return;
    }

    setLoading('send');
    try {
      const response = await offerLetterApi.sendRelievingLetter(employee._id, {
        ...buildPayload(),
        to: emailTo.trim(),
        ...(emailSubject.trim() ? { subject: emailSubject.trim() } : {}),
        ...(emailMessage.trim() ? { message: emailMessage.trim() } : {}),
      });

      if (!response.success) {
        alert(response.error || 'Failed to send relieving letter.');
        return;
      }

      alert(
        response.message ||
          `Relieving letter sent to ${response.data?.to || emailTo.trim()}.`,
      );
      onClose();
    } catch (error: unknown) {
      await handleApiError(error, 'Failed to send relieving letter. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const isBusy = loading !== null;

  return (
    <div className="fixed inset-0 backdrop-blur-md bg-black/20 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-0">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Generate Relieving Letter</h2>
              <p className="text-sm text-gray-600 mt-1">
                {employee.firstName} {employee.lastName} · {employee.employeeId}
              </p>
            </div>
            <Button variant="outline" onClick={onClose} className="rounded-full hover:bg-gray-100">
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleDownload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Working Day
              </label>
              <input
                type="date"
                value={lastWorkingDay}
                onChange={(e) => setLastWorkingDay(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Summary
              </label>
              <textarea
                value={employmentSummary}
                onChange={(e) => setEmploymentSummary(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Authorized Signatory
              </label>
              <input
                type="text"
                value={authorizedSignatory}
                onChange={(e) => setAuthorizedSignatory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Email (optional)</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send To
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder={employee.email || 'employee@example.com'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Your Relieving Letter - Nowthingstodo Worldwide Ltd"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                  placeholder="Leave blank to use the default email message."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={onClose} disabled={isBusy}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSendEmail}
                disabled={isBusy}
              >
                {loading === 'send' ? 'Sending...' : 'Send Email'}
              </Button>
              <Button type="submit" disabled={isBusy} className="bg-blue-600 hover:bg-blue-700">
                {loading === 'download' ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
